import { useEffect, useRef } from 'react'
import { db } from '../lib/db'
import type { CrmName } from '../lib/crm'
import { useCrmConnections } from './useCrmConnections'
import { useActiveCrm } from './useCrmMapping'
import { syncConversationFromDexie } from './useConversationSync'

const AUTO_SYNC_RETRY_FLOOR_MS = 5_000

export function useCrmAutoSync() {
  const { connections } = useCrmConnections()
  const activeCrm = useActiveCrm(connections)
  const ready = activeCrm === 'affinity'
    && connections.affinity?.status === 'active'
    && Boolean(connections.affinity?.enrichmentEnabled)

  const activeCrmRef = useRef<CrmName | null>(activeCrm)
  const readyRef = useRef(ready)
  const inFlightRef = useRef(new Set<string>())
  const lastAttemptRef = useRef(new Map<string, number>())

  useEffect(() => {
    activeCrmRef.current = activeCrm
    readyRef.current = ready
  }, [activeCrm, ready])

  useEffect(() => {
    const maybeAutoSync = async (conversationUrn: string) => {
      const crmName = activeCrmRef.current
      if (!readyRef.current || !crmName) return

      const lockKey = `${crmName}:${conversationUrn}`
      if (inFlightRef.current.has(lockKey)) return

      const lastAttempt = lastAttemptRef.current.get(lockKey) || 0
      if (Date.now() - lastAttempt < AUTO_SYNC_RETRY_FLOOR_MS) return

      inFlightRef.current.add(lockKey)
      lastAttemptRef.current.set(lockKey, Date.now())
      try {
        const shouldSync = await shouldAutoSyncConversation(conversationUrn, crmName)
        if (!shouldSync) return

        const report = await syncConversationFromDexie({ conversationUrn, crmName })
        if (report?.toSync) {
          console.log(
            '[CRM Auto Sync] Synced',
            report.toSync,
            'new messages for',
            conversationUrn,
          )
        }
      } catch (e) {
        console.warn('[CRM Auto Sync] Failed:', e)
      } finally {
        inFlightRef.current.delete(lockKey)
      }
    }

    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail
      if (detail?.type !== 'messages') return

      const conversationUrns = Array.isArray(detail.conversationUrns)
        ? detail.conversationUrns.filter((urn: unknown): urn is string => typeof urn === 'string')
        : []
      for (const conversationUrn of conversationUrns) {
        void maybeAutoSync(conversationUrn)
      }
    }

    window.addEventListener('fwdslash-data-updated', handler)
    return () => window.removeEventListener('fwdslash-data-updated', handler)
  }, [])
}

async function shouldAutoSyncConversation(conversationUrn: string, crmName: CrmName) {
  const conversation = await db.conversations.get(conversationUrn)
  if (!conversation || conversation.isGroup || !conversation.participantProfileUrn) return false

  const mapping = await db.crmMappings.get([conversation.participantProfileUrn, crmName])
  if (mapping?.status !== 'matched' && mapping?.status !== 'created') return false

  const messages = await db.messages
    .where('conversationUrn')
    .equals(conversationUrn)
    .toArray()
  const textMessages = messages.filter((message) => (message.text || '').trim().length > 0)
  if (textMessages.length === 0) return false

  const keys = textMessages.map((message) => [message.messageUrn, crmName] as [string, string])
  const syncedRows = await db.crmMessageSync.bulkGet(keys)
  const hasPreviousConfirmedSync = syncedRows.some(Boolean)
  const hasUnsyncedMessages = syncedRows.some((row) => !row)

  return hasPreviousConfirmedSync && hasUnsyncedMessages
}
